import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { COMMON_ROUTES, RouteEnum } from './app-routing.module';
import { AppFeatures } from './app.features';

import { AppConfigService } from './app-config.service';
import { AuthMfService } from './shared/services/auth-mf.service';
import { FeatureDecisionService } from './lib-feature-decision/services/feature-decision/feature-decision.service';
import { Auth0Service } from './core/services/auth0.service';

import { Store } from '@ngxs/store';

import { LoadPersonne } from './shared/personne.state';
import { Load as LoadPreautorisation } from './tool-preautorisation/experimente/state/preautorisation.actions';
import { ChargerCapacite } from './tool-capacite/latest/capacite.actions';
import { LoadMassNotifications, LoadNotifications } from './notification/notification.state';
import { ChargerProprietes } from './propriete/propriete.state';
import { LoadOctroi } from './octroi/octroi.state';

enum TypeErreurEnum {
  ChargementSession = 'ChargementSession',
  NonAuthentifie = 'NonAuthentifie',
  ChargementDonnees = 'ChargementDonnees',
  FusionPdoActive = 'FusionPdoActive',
  NonEligible = 'NonEligible'
}

class ErreurInitialisation {
  constructor(readonly type: TypeErreurEnum, readonly route: string) {}
}

@Injectable({
  providedIn: 'root'
})
export class AppInitialisationService {
  private initialRoute: string;

  constructor(
    private readonly appConfigService: AppConfigService,
    private readonly decisionService: FeatureDecisionService,
    private readonly router: Router,
    private readonly authMfService: AuthMfService,
    private readonly store: Store,
    private readonly auth0Service: Auth0Service
  ) {}

  async initialiser() {
    try {
      await this.auth0Service.initialiser();
      await this.initialiserSession();
      await this.validerIdentiteMembre();
      await this.chargerDonnees();
    } catch (erreur) {
      this.gererErreur(erreur);
    } finally {
      await this.chargerRoutes();
    }
  }

  private gererErreur(erreur) {
    if (erreur instanceof ErreurInitialisation) {
      // TODO log les erreurs au backend pour les retrouver dans splunk
      this.initialRoute = erreur.route;
    } else {
      this.initialRoute = RouteEnum.Erreur;
    }
  }

  private async initialiserSession() {
    try {
      await this.appConfigService.loadAppConfig();
    } catch (error) {
      this.appConfigService.loadDefaultConfig();
      throw new ErreurInitialisation(TypeErreurEnum.ChargementSession, RouteEnum.Erreur);
    }

    if (this.appConfigService.isPDOFusionPending) {
      throw new ErreurInitialisation(TypeErreurEnum.FusionPdoActive, RouteEnum.Erreur);
    }

    if (this.appConfigService.configData.nonEligible) {
      throw new ErreurInitialisation(TypeErreurEnum.NonEligible, RouteEnum.NonEligible);
    }
  }

  private async validerIdentiteMembre() {
    try {
      await this.authMfService.authenticateTransaction();
      await this.store.dispatch(new LoadPersonne()).toPromise();
    } catch {
      throw new ErreurInitialisation(TypeErreurEnum.NonAuthentifie, RouteEnum.NonAuthentifie);
    }
  }

  private async chargerDonnees() {
    try {
      await Promise.all([
        this.appConfigService.loadAppDonneReference(),
        this.store.dispatch(new LoadPreautorisation()).toPromise(),
        this.store.dispatch(new ChargerCapacite()).toPromise(),
        this.store.dispatch(new LoadNotifications()).toPromise(),
        this.store.dispatch(new LoadMassNotifications()).toPromise(),
        this.store.dispatch(new ChargerProprietes()).toPromise(),
        this.store.dispatch(new LoadOctroi()).toPromise()
      ]);
    } catch {
      throw new ErreurInitialisation(TypeErreurEnum.ChargementDonnees, RouteEnum.Erreur);
    }
  }

  private async chargerRoutes() {
    const parcoursMPM = () => import('./parcours-mpm/parcours-mpm.module').then(m => m.ParcoursMpmModule);
    const parcoursDSD = () => import('./parcours-dsd/parcours-dsd.module').then(m => m.ParcoursDsdModule);
    const isParcoursDSD = this.decisionService.evaluate(AppFeatures.ParcoursDSD);
    this.router.resetConfig([
      {
        path: '',
        loadChildren: isParcoursDSD ? parcoursDSD : parcoursMPM
      },
      ...COMMON_ROUTES
    ]);

    this.router.initialNavigation();

    if (this.initialRoute) {
      await this.router.navigateByUrl(this.initialRoute);
    }
  }
}
